import React from 'react';
import { FileText, Upload, X } from 'lucide-react';
import SearchableDropdown from './SearchableDropdown';
import MonthYearPicker from './components/shared/MonthYearPicker';
import AIImproveButton from './components/shared/AIImproveButton';
import LinkedInLogo from './components/shared/LinkedInLogo';
import LinkedInHelpPopup from './components/shared/LinkedInHelpPopup';

/**
 * Component for rendering CV form content in the Guide tab
 */
const GuideContent = ({
  // Current section and frame
  walkthroughCurrentFrame,
  getCurrentSection,
  getTotalFrames,

  // Data state
  anquetData,
  uploadedFile,
  setUploadedFile,

  // Taxonomy data
  taxonomyData,
  taxonomyLoading,

  // Field change handlers
  handleWalkthroughFieldChange,
  handleWalkthroughExperienceFieldChange,
  handleWalkthroughEducationFieldChange,
  handleWalkthroughLanguageFieldChange,
  handleWalkthroughQualificationFieldChange,
  handleWalkthroughAwardFieldChange,
  handleWalkthroughSocialLinkFieldChange,
  setAnquetData,
  setHasUnsavedChanges,

  // Array handlers
  addWalkthroughExperience,
  removeWalkthroughExperience,
  addWalkthroughEducation,
  removeWalkthroughEducation,
  addWalkthroughLanguage,
  removeWalkthroughLanguage,
  addWalkthroughQualification,
  removeWalkthroughQualification,
  addWalkthroughAward,
  removeWalkthroughAward,
  addWalkthroughSocialLink,
  removeWalkthroughSocialLink,

  // File upload handlers
  dragActive,
  handleDrag,
  handleDrop,
  handleFileChange,

  // LinkedIn help
  showLinkedInHelp,
  setShowLinkedInHelp,
  linkedInIconRef,
  setLinkedInIconRef,

  // AI improvement
  hoveredField,
  setHoveredField,
  improveWalkthroughAboutWithAI,
  isImprovingField,
  improvementTarget,

  // Date parsing
  parseTimeframe,
  combineTimeframe,
  isValidYearMonth,

  // Keyboard handler
  handleKeyDown,

  // AI processing state
  isProcessing
}) => {
  // Helper to safely check if a field value is empty (handles strings and arrays)
  const isFieldEmpty = (value) => {
    if (Array.isArray(value)) {
      return !value.some(item => typeof item === 'string' && item.trim());
    }
    if (typeof value === 'string') {
      return !value.trim();
    }
    return !value;
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {getCurrentSection() && (
        <div className="flex w-full flex-col gap-4 pb-[100px] h-full overflow-y-auto box-border scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
          {walkthroughCurrentFrame === 1 ? (
            // Special layout for About/Upload section
            <div className="flex flex-col gap-5">
              {/* Intake: About Yourself (text only) + optional file Apply */}
              <div className="walkthrough-field walkthrough-field-full">
                {uploadedFile && (
                  <label className="walkthrough-field-label">
                    (Optional) If you want to let our AI something additional to the uploaded CV, you can do it here.
                  </label>
                )}
                <div className="text-input-container" data-onboard="walkthrough-text">
                  {/* LinkedIn Help Icon (hover only) */}
                  <div
                    ref={(ref) => setLinkedInIconRef(ref)}
                    className="linkedin-help-icon"
                    onMouseEnter={() => setShowLinkedInHelp(true)}
                    onMouseLeave={() => setShowLinkedInHelp(false)}
                  >
                    <LinkedInLogo />
                  </div>

                  {/* LinkedIn Help Popup */}
                  {showLinkedInHelp && linkedInIconRef && (
                    <LinkedInHelpPopup iconRef={linkedInIconRef} />
                  )}

                  <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                    <textarea
                      className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                      placeholder="Copy and paste your linked in page contents here (or comments for our AI additional to the uploaded CV)"
                      value={anquetData?.initial_cv_input || ''}
                      onChange={(e) => handleWalkthroughFieldChange('initial_cv_input', e.target.value)}
                      rows="4"
                      disabled={isProcessing}
                      readOnly={false}
                    />
                  </div>
                </div>

                <div className="divider-text">... or add your old CV</div>
                <div
                  className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${uploadedFile ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('walkthrough-file-input')?.click()}
                  data-onboard="file-upload"
                >
                  <input
                    id="walkthrough-file-input"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt"
                    style={{ display: 'none' }}
                  />

                  {uploadedFile ? (
                    <div className="file-info">
                      <FileText size={24} />
                      <span>{uploadedFile.name}</span>
                      <button
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="file-drop-content">
                      <Upload size={32} />
                      <p>Drag & drop your resume/CV here</p>
                      <span>or click to browse files</span>
                      <small>Supports PDF, DOC, DOCX, TXT</small>
                    </div>
                  )}
                </div>
                {/* Apply button removed from walkthrough first page; moved to Next */}
              </div>
            </div>
          ) : getCurrentSection().type === 'experience_list' ? (
            // Experience list layout
            <div className="walkthrough-experience-layout">
              {(anquetData?.experience || []).map((experience, expIndex) => (
                <section key={expIndex} className="relative my-4 flex w-full flex-col gap-6 rounded-md border border-dashed border-gray-300 p-6">
                  {(anquetData?.experience || []).length >= 1 && (
                    <button
                      type="button"
                      className="absolute right-2 top-1 text-brand-orange-600 flex h-8 w-8 items-center justify-center rounded-full border border-solid border-brand-orange-300 transition duration-300 ease-in-out hover:bg-brand-orange-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      onClick={() => removeWalkthroughExperience(expIndex)}
                      title="Remove this experience"
                      disabled={isProcessing}
                    >
                      <svg width="12" height="4" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 1.9998C0 1.68154 0.126428 1.37632 0.351472 1.15128C0.576516 0.926233 0.88174 0.799805 1.2 0.799805H10.8C11.1183 0.799805 11.4235 0.926233 11.6485 1.15128C11.8736 1.37632 12 1.68154 12 1.9998C12 2.31806 11.8736 2.62329 11.6485 2.84833C11.4235 3.07338 11.1183 3.1998 10.8 3.1998H1.2C0.88174 3.1998 0.576516 3.07338 0.351472 2.84833C0.126428 2.62329 0 2.31806 0 1.9998Z" fill="currentColor"/>
                      </svg>
                    </button>
                  )}

                  {/* Role and Company in a row */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                    {getCurrentSection().fields.filter(f => ['role', 'company'].includes(f.name)).sort((a, b) => {
                      const order = ['role', 'company'];
                      return order.indexOf(a.name) - order.indexOf(b.name);
                    }).map(field => (
                      <div key={field.name} className="walkthrough-field walkthrough-field-half">
                          <label className="walkthrough-field-label">
                            {field.label}
                            {field.required && isFieldEmpty(experience[field.name]) && <span className="required-indicator">*</span>}
                          </label>
                          <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                            <input
                              type={field.type}
                              className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                              placeholder={field.placeholder}
                              value={experience[field.name] || ''}
                              onChange={(e) => handleWalkthroughExperienceFieldChange(expIndex, field.name, e.target.value)}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                          {field.example && (
                            <small className="walkthrough-example">Example: {field.example}</small>
                          )}
                        </div>
                      ))}
                    </div>

                  {/* Timeframe (Start and End Date) - Full width above location */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                    {getCurrentSection().fields.filter(f => ['timeframe'].includes(f.name)).map(field => (
                      <React.Fragment key={field.name}>
                          <div className="walkthrough-field walkthrough-field-half">
                            <label className="paragraph_medium_bold text-gray-700">Start date{field.required && isFieldEmpty(experience.start_date) && <span className="required-indicator">*</span>}</label>
                            <MonthYearPicker
                              value={(experience.start_date && isValidYearMonth(experience.start_date)) ? experience.start_date : parseTimeframe(experience.timeframe).start}
                              onChange={(start) => {
                                const present = experience.is_current || parseTimeframe(experience.timeframe).present;
                                const end = experience.end_date || parseTimeframe(experience.timeframe).end;
                                const tf = combineTimeframe(start, end, present);
                                handleWalkthroughExperienceFieldChange(expIndex, 'start_date', (start === 'Present' ? '' : start));
                                handleWalkthroughExperienceFieldChange(expIndex, 'timeframe', tf);
                              }}
                              placeholder="YYYY-MM"
                              disabled={taxonomyLoading || isProcessing}
                            />
                          </div>
                          <div className="walkthrough-field walkthrough-field-half">
                            <label className="paragraph_medium_bold text-gray-700">End date{field.required && isFieldEmpty(experience.end_date) && !experience.is_current && <span className="required-indicator">*</span>}</label>
                            <MonthYearPicker
                              value={(experience.is_current ? 'Present' : (experience.end_date && isValidYearMonth(experience.end_date)) ? experience.end_date : (parseTimeframe(experience.timeframe).present ? 'Present' : parseTimeframe(experience.timeframe).end))}
                              onChange={(end) => {
                                const start = experience.start_date || parseTimeframe(experience.timeframe).start;
                                const presentFlag = end === 'Present';
                                const endVal = presentFlag ? '' : end;
                                const tf = combineTimeframe(start, endVal, presentFlag);
                                handleWalkthroughExperienceFieldChange(expIndex, 'end_date', endVal);
                                handleWalkthroughExperienceFieldChange(expIndex, 'is_current', presentFlag);
                                handleWalkthroughExperienceFieldChange(expIndex, 'timeframe', tf);
                              }}
                              placeholder="YYYY-MM or Present"
                              allowPresent={true}
                              disabled={taxonomyLoading || isProcessing}
                            />
                          </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Country and City in a row */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                      {/* Country */}
                      {getCurrentSection().fields.filter(f => ['country'].includes(f.name)).map(field => (
                        <div key={field.name} className="walkthrough-field walkthrough-field-half">
                          <label className="walkthrough-field-label">
                            {field.label}
                            {field.required && isFieldEmpty(experience[field.name]) && <span className="required-indicator">*</span>}
                          </label>
                          <SearchableDropdown
                            options={taxonomyData.countries}
                            value={experience[field.name] || ''}
                            onChange={(value) => handleWalkthroughExperienceFieldChange(expIndex, field.name, value)}
                            placeholder={field.placeholder}
                            disabled={taxonomyLoading || isProcessing}
                            className="walkthrough-dropdown"
                          />
                          {field.example && (
                            <small className="walkthrough-example">Example: {field.example}</small>
                          )}
                        </div>
                      ))}
                      {/* City */}
                      {getCurrentSection().fields.filter(f => ['city'].includes(f.name)).map(field => (
                        <div key={field.name} className="walkthrough-field walkthrough-field-half">
                          <label className="walkthrough-field-label">
                            {field.label}
                            {field.required && isFieldEmpty(experience[field.name]) && <span className="required-indicator">*</span>}
                          </label>
                          <SearchableDropdown
                            options={taxonomyData.cities}
                            value={experience[field.name] || ''}
                            onChange={(value) => handleWalkthroughExperienceFieldChange(expIndex, field.name, value)}
                            placeholder={field.placeholder}
                            disabled={taxonomyLoading || isProcessing}
                            className="walkthrough-dropdown"
                          />
                          {field.example && (
                            <small className="walkthrough-example">Example: {field.example}</small>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Description full width */}
                    {getCurrentSection().fields.filter(f => f.name === 'description').map(field => (
                      <div key={field.name} className="walkthrough-field walkthrough-field-full">
                        <label className="walkthrough-field-label">
                          {field.label}
                          {field.required && isFieldEmpty(experience[field.name]) && <span className="required-indicator">*</span>}
                        </label>
                        <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                          <textarea
                            className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                            placeholder={field.placeholder}
                            value={experience[field.name] || ''}
                            onChange={(e) => handleWalkthroughExperienceFieldChange(expIndex, field.name, e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows="4"
                          />
                        </div>
                        {field.example && (
                          <small className="walkthrough-example">Example: {field.example}</small>
                        )}
                      </div>
                    ))}
                </section>
              ))}

              <div className="flex items-center gap-3 mt-5 self-end">
                <span className="paragraph_medium_regular text-gray-500">Add work experience</span>
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-gray-300 transition duration-300 ease-in-out hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  type="button"
                  onClick={addWalkthroughExperience}
                  disabled={isProcessing}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.5 4.875H7.125V1.5C7.125 1.10156 6.77344 0.75 6.375 0.75H5.625C5.20312 0.75 4.875 1.10156 4.875 1.5V4.875H1.5C1.07812 4.875 0.75 5.22656 0.75 5.625V6.375C0.75 6.79688 1.07812 7.125 1.5 7.125H4.875V10.5C4.875 10.9219 5.20312 11.25 5.625 11.25H6.375C6.77344 11.25 7.125 10.9219 7.125 10.5V7.125H10.5C10.8984 7.125 11.25 6.79688 11.25 6.375V5.625C11.25 5.22656 10.8984 4.875 10.5 4.875Z" fill="#5A5A5A"/>
                  </svg>
                </button>
              </div>
            </div>
          ) : getCurrentSection().type === 'education_list' ? (
            // Education list layout
            <div className="walkthrough-education-layout">
              {(anquetData?.education || []).map((education, eduIndex) => (
                <section key={eduIndex} className="relative my-4 flex w-full flex-col gap-6 rounded-md border border-dashed border-gray-300 p-6">
                  {(anquetData?.education || []).length >= 1 && (
                    <button
                      type="button"
                      className="absolute right-2 top-1 text-brand-orange-600 flex h-8 w-8 items-center justify-center rounded-full border border-solid border-brand-orange-300 transition duration-300 ease-in-out hover:bg-brand-orange-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      onClick={() => removeWalkthroughEducation(eduIndex)}
                      title="Remove this education"
                      disabled={isProcessing}
                    >
                      <svg width="12" height="4" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 1.9998C0 1.68154 0.126428 1.37632 0.351472 1.15128C0.576516 0.926233 0.88174 0.799805 1.2 0.799805H10.8C11.1183 0.799805 11.4235 0.926233 11.6485 1.15128C11.8736 1.37632 12 1.68154 12 1.9998C12 2.31806 11.8736 2.62329 11.6485 2.84833C11.4235 3.07338 11.1183 3.1998 10.8 3.1998H1.2C0.88174 3.1998 0.576516 3.07338 0.351472 2.84833C0.126428 2.62329 0 2.31806 0 1.9998Z" fill="currentColor"/>
                      </svg>
                    </button>
                  )}

                  {/* Level and Institution in a row */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                      {getCurrentSection().fields.filter(f => ['level', 'institution'].includes(f.name)).map(field => (
                        <div key={field.name} className="walkthrough-field walkthrough-field-half">
                          <label className="walkthrough-field-label">
                            {field.label}
                            {field.required && isFieldEmpty(education[field.name]) && <span className="required-indicator">*</span>}
                          </label>
                          {field.type === 'dropdown' ? (
                            <SearchableDropdown
                              options={taxonomyData.educationLevels}
                              value={education[field.name] || ''}
                              onChange={(value) => handleWalkthroughEducationFieldChange(eduIndex, field.name, value)}
                              placeholder={field.placeholder}
                              disabled={taxonomyLoading || isProcessing}
                              className="walkthrough-dropdown"
                            />
                          ) : (
                            <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                              <input
                                type={field.type}
                                className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                                placeholder={field.placeholder}
                                value={education[field.name] || ''}
                                onChange={(e) => handleWalkthroughEducationFieldChange(eduIndex, field.name, e.target.value)}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          )}
                          {field.example && (
                            <small className="walkthrough-example">Example: {field.example}</small>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Items full width */}
                    {getCurrentSection().fields.filter(f => f.name === 'items').map(field => (
                      <div key={field.name} className="walkthrough-field walkthrough-field-full">
                        <label className="walkthrough-field-label">
                          {field.label}
                          {field.required && isFieldEmpty(education[field.name]) && <span className="required-indicator">*</span>}
                        </label>
                        <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                          <textarea
                            className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                            placeholder={field.placeholder}
                            value={education[field.name] || ''}
                            onChange={(e) => handleWalkthroughEducationFieldChange(eduIndex, field.name, e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows="3"
                          />
                        </div>
                        {field.example && (
                          <small className="walkthrough-example">Example: {field.example}</small>
                        )}
                      </div>
                    ))}

                  {/* Timeframe (Start and End Date) */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                    {getCurrentSection().fields.filter(f => f.name === 'timeframe').map(field => (
                      <React.Fragment key={field.name}>
                          <div className="walkthrough-field walkthrough-field-half">
                            <label className="paragraph_medium_bold text-gray-700">Start{field.required && isFieldEmpty(education.start_date) && <span className="required-indicator">*</span>}</label>
                            <MonthYearPicker
                              value={(education.start_date && isValidYearMonth(education.start_date)) ? education.start_date : parseTimeframe(education.timeframe).start}
                              onChange={(start) => {
                                const present = education.is_current || parseTimeframe(education.timeframe).present;
                                const end = education.end_date || parseTimeframe(education.timeframe).end;
                                const tf = combineTimeframe(start, end, present);
                                handleWalkthroughEducationFieldChange(eduIndex, 'start_date', (start === 'Present' ? '' : start));
                                handleWalkthroughEducationFieldChange(eduIndex, 'timeframe', tf);
                              }}
                              placeholder="YYYY-MM"
                              disabled={taxonomyLoading || isProcessing}
                            />
                          </div>
                          <div className="walkthrough-field walkthrough-field-half">
                            <label className="paragraph_medium_bold text-gray-700">End{field.required && isFieldEmpty(education.end_date) && !education.is_current && <span className="required-indicator">*</span>}</label>
                            <MonthYearPicker
                              value={(education.is_current ? 'Present' : (education.end_date && isValidYearMonth(education.end_date)) ? education.end_date : (parseTimeframe(education.timeframe).present ? 'Present' : parseTimeframe(education.timeframe).end))}
                              onChange={(end) => {
                                const start = education.start_date || parseTimeframe(education.timeframe).start;
                                const presentFlag = end === 'Present';
                                const endVal = presentFlag ? '' : end;
                                const tf = combineTimeframe(start, endVal, presentFlag);
                                handleWalkthroughEducationFieldChange(eduIndex, 'end_date', endVal);
                                handleWalkthroughEducationFieldChange(eduIndex, 'is_current', presentFlag);
                                handleWalkthroughEducationFieldChange(eduIndex, 'timeframe', tf);
                              }}
                              placeholder="YYYY-MM or Present"
                              allowPresent={true}
                              disabled={taxonomyLoading || isProcessing}
                            />
                          </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Country and City in a row */}
                  <div className="flex w-full flex-col gap-6 md:flex-row">
                      {getCurrentSection().fields.filter(f => ['country', 'city'].includes(f.name)).map(field => (
                        <div key={field.name} className="walkthrough-field walkthrough-field-half">
                          <label className="walkthrough-field-label">
                            {field.label}
                            {field.required && isFieldEmpty(education[field.name]) && <span className="required-indicator">*</span>}
                          </label>
                          <SearchableDropdown
                            options={field.name === 'country' ? taxonomyData.countries : taxonomyData.cities}
                            value={education[field.name] || ''}
                            onChange={(value) => handleWalkthroughEducationFieldChange(eduIndex, field.name, value)}
                            placeholder={field.placeholder}
                            disabled={taxonomyLoading || isProcessing}
                            className="walkthrough-dropdown"
                          />
                          {field.example && (
                            <small className="walkthrough-example">Example: {field.example}</small>
                          )}
                        </div>
                      ))}
                    </div>
                </section>
              ))}

              <div className="flex items-center gap-3 mt-5 self-end">
                <span className="paragraph_medium_regular text-gray-500">Add education</span>
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-gray-300 transition duration-300 ease-in-out hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  type="button"
                  onClick={addWalkthroughEducation}
                  disabled={isProcessing}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.5 4.875H7.125V1.5C7.125 1.10156 6.77344 0.75 6.375 0.75H5.625C5.20312 0.75 4.875 1.10156 4.875 1.5V4.875H1.5C1.07812 4.875 0.75 5.22656 0.75 5.625V6.375C0.75 6.79688 1.07812 7.125 1.5 7.125H4.875V10.5C4.875 10.9219 5.20312 11.25 5.625 11.25H6.375C6.77344 11.25 7.125 10.9219 7.125 10.5V7.125H10.5C10.8984 7.125 11.25 6.79688 11.25 6.375V5.625C11.25 5.22656 10.8984 4.875 10.5 4.875Z" fill="#5A5A5A"/>
                  </svg>
                </button>
              </div>
            </div>
          ) : getCurrentSection().title === 'Personal Information' ? (
            // Personal Information layout with grouped fields
            <div className="flex flex-col gap-5">
              {/* Half width: First Name + Last Name */}
              <div className="flex gap-4 items-start">
                {getCurrentSection().fields.filter(f => ['first_name', 'last_name'].includes(f.name)).map(field => (
                  <div key={field.name} className="walkthrough-field walkthrough-field-half">
                    <label className="walkthrough-field-label">
                      {field.label}
                      {field.required && isFieldEmpty(anquetData?.personal?.[field.name]) && <span className="required-indicator">*</span>}
                    </label>
                    <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                      <input
                        type={field.type}
                        className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                        placeholder={field.placeholder}
                        value={anquetData?.personal?.[field.name] || ''}
                        onChange={(e) => handleWalkthroughFieldChange(field.name, e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    {field.example && (
                      <small className="walkthrough-example">Example: {field.example}</small>
                    )}
                  </div>
                ))}
              </div>

              {/* Half width: Email + Phone */}
              <div className="flex gap-4 items-start">
                {getCurrentSection().fields.filter(f => ['email', 'phone'].includes(f.name)).map(field => (
                  <div key={field.name} className="walkthrough-field walkthrough-field-half">
                    <label className="walkthrough-field-label">
                      {field.label}
                      {field.required && isFieldEmpty(anquetData?.personal?.[field.name]) && <span className="required-indicator">*</span>}
                    </label>
                    <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                      <input
                        type={field.type}
                        className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                        placeholder={field.placeholder}
                        value={anquetData?.personal?.[field.name] || ''}
                        onChange={(e) => handleWalkthroughFieldChange(field.name, e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    {field.example && (
                      <small className="walkthrough-example">Example: {field.example}</small>
                    )}
                  </div>
                ))}
              </div>

              {/* Half width: Country + City */}
              <div className="flex gap-4 items-start">
                {getCurrentSection().fields.filter(f => ['country', 'city'].includes(f.name)).map(field => (
                  <div key={field.name} className="walkthrough-field walkthrough-field-half">
                    <label className="walkthrough-field-label">
                      {field.label}
                      {field.required && isFieldEmpty(anquetData?.personal?.[field.name]) && <span className="required-indicator">*</span>}
                    </label>
                    <SearchableDropdown
                      options={
                        field.name === 'country' ? taxonomyData.countries :
                        field.name === 'city' ? taxonomyData.cities :
                        []
                      }
                      value={anquetData?.personal?.[field.name] || ''}
                      onChange={(value) => handleWalkthroughFieldChange(field.name, value)}
                      placeholder={field.placeholder}
                      disabled={taxonomyLoading || isProcessing}
                      className="walkthrough-dropdown"
                    />
                    {field.example && (
                      <small className="walkthrough-example">Example: {field.example}</small>
                    )}
                  </div>
                ))}
              </div>

              {/* Full width: Job Categories (Multi-select) */}
              {getCurrentSection().fields.filter(f => f.name === 'job_category').map(field => (
                <div key={field.name} className="walkthrough-field walkthrough-field-full">
                  <label className="walkthrough-field-label">
                    {field.label}
                    {field.required && !(anquetData?.personal?.job_categories?.length > 0) && <span className="required-indicator">*</span>}
                  </label>
                  <div className="skills-multi-select">
                    <SearchableDropdown
                      options={taxonomyData.jobCategories
                        .filter(category => !(anquetData?.personal?.job_categories || []).includes(category))}
                      value=""
                      onChange={(value) => {
                        if (value && !(anquetData?.personal?.job_categories || []).includes(value)) {
                          const newCategories = [...(anquetData?.personal?.job_categories || []), value];
                          setAnquetData(prev => ({
                            ...(prev || {}),
                            personal: {
                              ...(prev?.personal || {}),
                              job_categories: newCategories
                            }
                          }));
                          setHasUnsavedChanges(true);
                        }
                      }}
                      placeholder={field.placeholder}
                      disabled={taxonomyLoading || isProcessing}
                      className="walkthrough-dropdown"
                    />
                    <div className="skills-selected">
                      {(anquetData?.personal?.job_categories || []).map((category, index) => (
                        <button
                          key={index}
                          className="button_small_bold py-1 px-3 md:button_large_bold md:py-2 md:px-4 flex whitespace-nowrap font-bold leading-6 tracking-sm disabled:opacity-30 items-center justify-center gap-2 tracking-wide transition duration-200 ease-out disabled:cursor-not-allowed bg-transparent text-brand-orange-600 hover:brightness-125 border-current border border-solid rounded-full"
                          onClick={() => {
                            const newCategories = (anquetData?.personal?.job_categories || []).filter((_, i) => i !== index);
                            setAnquetData(prev => ({
                              ...(prev || {}),
                              personal: {
                                ...(prev?.personal || {}),
                                job_categories: newCategories
                              }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                  {field.example && (
                    <small className="walkthrough-example">Example: {field.example}</small>
                  )}
                </div>
              ))}
            </div>
          ) : (
          // Default layout for other sections
            <>
            {getCurrentSection().fields?.map((field, index) => (
              <div key={field.name} className={`walkthrough-field ${field.name === 'about' ? 'form-field-with-ai' : ''}`}
                   onMouseEnter={field.name === 'about' ? () => setHoveredField('walkthrough_about') : undefined}
                   onMouseLeave={field.name === 'about' ? () => setHoveredField(null) : undefined}>
                <label className="walkthrough-field-label">
                  {field.label}
                  {field.required && isFieldEmpty(field.name === 'about' ? anquetData?.about : anquetData?.personal?.[field.name]) && <span className="required-indicator">*</span>}
                </label>

                {field.type === 'textarea' ? (
                  <>
                    {field.name === 'about' ? (
                      <div className="text-input-container">
                        {/* LinkedIn Help Icon (hover only) moved to About field */}
                        <div
                          ref={(ref) => setLinkedInIconRef(ref)}
                          className="linkedin-help-icon"
                          onMouseEnter={() => setShowLinkedInHelp(true)}
                          onMouseLeave={() => setShowLinkedInHelp(false)}
                        >
                          <LinkedInLogo />
                        </div>

                        {/* LinkedIn Help Popup */}
                        {showLinkedInHelp && linkedInIconRef && (
                          <LinkedInHelpPopup iconRef={linkedInIconRef} />
                        )}

                        <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                          <textarea
                            className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                            placeholder={field.placeholder}
                            value={anquetData?.about || ''}
                            onChange={(e) => handleWalkthroughFieldChange(field.name, e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows="4"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                        <textarea
                          className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                          placeholder={field.placeholder}
                          value={anquetData?.personal?.[field.name] || ''}
                          onChange={(e) => handleWalkthroughFieldChange(field.name, e.target.value)}
                          onKeyDown={handleKeyDown}
                          rows="4"
                        />
                      </div>
                    )}

                    {field.name === 'about' && (
                      <>
                        <AIImproveButton
                          fieldType="about"
                          currentText={anquetData?.about || ''}
                          isVisible={hoveredField === 'walkthrough_about'}
                          onImprove={improveWalkthroughAboutWithAI}
                          isImproving={isImprovingField && improvementTarget?.type === 'about' && improvementTarget?.index === null}
                          style={{ top: 44 }}
                        />

                        {/* Drag & Drop moved under About field */}
                        <div className="divider-text">... or add your old CV</div>
                        <div
                          className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${uploadedFile ? 'has-file' : ''}`}
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                          onClick={() => document.getElementById('walkthrough-file-input')?.click()}
                        >
                          <input
                            id="walkthrough-file-input"
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.txt"
                            style={{ display: 'none' }}
                          />

                          {uploadedFile ? (
                            <div className="file-info">
                              <FileText size={24} />
                              <span>{uploadedFile.name}</span>
                              <button
                                className="remove-file"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUploadedFile(null);
                                }}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="file-drop-content">
                              <Upload size={32} />
                              <p>Drag & drop your resume/CV here</p>
                              <span>or click to browse files</span>
                              <small>Supports PDF, DOC, DOCX, TXT</small>
                            </div>
                          )}
                        </div>
                        {/* Apply button removed: functionality moved to Next button */}
                      </>
                    )}
                  </>
                ) : field.type === 'dropdown' ? (
                  <SearchableDropdown
                    options={
                      field.name === 'country' ? taxonomyData.countries :
                      field.name === 'city' ? taxonomyData.cities :
                      []
                    }
                    value={anquetData?.personal?.[field.name] || ''}
                    onChange={(value) => handleWalkthroughFieldChange(field.name, value)}
                    placeholder={field.placeholder}
                    disabled={taxonomyLoading || isProcessing}
                    className="walkthrough-dropdown"
                  />
                ) : (
                  <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                    <input
                      type={field.type}
                      className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                      placeholder={field.placeholder}
                      value={anquetData?.personal?.[field.name] || ''}
                      onChange={(e) => handleWalkthroughFieldChange(field.name, e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                )}

                {field.example && (
                  <small className="walkthrough-example">Example: {field.example}</small>
                )}
              </div>
            ))}

            {getCurrentSection().type === 'multi_list_section' && getCurrentSection().groups?.map((group, groupIndex) => (
              <div key={group.name} className="form-section">
                {group.type === 'multi_select' ? (
                  <div className="skills-multi-select">
                    <label className="walkthrough-field-label">
                      {group.title}
                      {group.required && !(anquetData?.[group.name]?.skills?.length > 0) && <span className="required-indicator">*</span>}
                    </label>
                    <SearchableDropdown
                      options={
                        group.name === 'soft_skills' ? taxonomyData.softSkills.filter(skill => !anquetData?.soft_skills?.skills?.includes(skill)) :
                        group.name === 'technical_skills' ? taxonomyData.technicalSkills.filter(skill => !anquetData?.technical_skills?.skills?.includes(skill)) :
                        []
                      }
                      value=""
                      onChange={(value) => {
                        if (value && !anquetData?.[group.name]?.skills?.includes(value)) {
                          setAnquetData(prev => ({
                            ...prev,
                            [group.name]: { skills: [...(prev[group.name]?.skills || []), value] }
                          }));
                          setHasUnsavedChanges(true);
                        }
                      }}
                      placeholder={group.placeholder}
                      disabled={taxonomyLoading || isProcessing}
                      className="skills-dropdown"
                    />
                    <div className="skills-selected">
                      {anquetData?.[group.name]?.skills?.map((skill, index) => (
                        <button
                          key={index}
                          className="button_small_bold py-1 px-3 md:button_large_bold md:py-2 md:px-4 flex whitespace-nowrap font-bold leading-6 tracking-sm disabled:opacity-30 items-center justify-center gap-2 tracking-wide transition duration-200 ease-out disabled:cursor-not-allowed bg-transparent text-brand-orange-600 hover:brightness-125 border-current border border-solid rounded-full"
                          onClick={() => {
                            const newSkills = anquetData[group.name].skills.filter((_, i) => i !== index);
                            setAnquetData(prev => ({
                              ...prev,
                              [group.name]: { skills: newSkills }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                          disabled={taxonomyLoading || isProcessing}
                        >
                          {skill}
                          <svg className="h-2.5 w-2.5" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L13 13M1 13L13 1L1 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : group.type === 'list' ? (
                  <>
                    <h6 className="section-heading">
                      {group.title}
                      {group.required && !((() => {
                        const listData = group.name === 'social_links' ? anquetData?.personal?.social_links : anquetData?.[group.name];
                        return listData?.some(item => group.fields?.every(f => !f.required || !isFieldEmpty(item[f.name])));
                      })()) && <span className="required-indicator">*</span>}
                    </h6>
                    <div className="array-fields-flat">
                      {(() => {
                        const getListData = () => {
                          if (group.name === 'social_links') return anquetData?.personal?.social_links || [];
                          return anquetData?.[group.name] || [];
                        };
                        const listData = getListData();

                        return (
                          <>
                            {listData.length > 0 && listData.map((item, itemIndex) => (
                              <div key={itemIndex} className="array-item-flat">
                                <div className="flex w-full flex-col items-center gap-4 lg:relative lg:flex-row lg:gap-6 lg:items-end lg:pr-[56px]">
                                  {group.fields?.map((field) => (
                                    <div key={field.name} className="flex w-full flex-col gap-2">
                                      <label className="paragraph_medium_bold text-gray-600">
                                        {field.label}
                                        {field.required && isFieldEmpty(item[field.name]) && <span className="required-indicator">*</span>}
                                      </label>
                                      {field.type === 'dropdown' ? (
                                        <SearchableDropdown
                                          options={
                                            field.name === 'language' ? taxonomyData.languages :
                                            field.name === 'proficiency' ? taxonomyData.proficiencyLevels :
                                            field.name === 'platform' ? taxonomyData.socialPlatforms :
                                            []
                                          }
                                          value={item[field.name] || ''}
                                          onChange={(value) => {
                                            const handlerName = `handleWalkthrough${group.name.charAt(0).toUpperCase() + group.name.slice(1).replace(/_([a-z])/g, (m, p1) => p1.toUpperCase())}FieldChange`;
                                            if (typeof window[handlerName] === 'function') {
                                              window[handlerName](itemIndex, field.name, value);
                                            } else if (group.name === 'languages') {
                                              handleWalkthroughLanguageFieldChange(itemIndex, field.name, value);
                                            } else if (group.name === 'qualifications') {
                                              handleWalkthroughQualificationFieldChange(itemIndex, field.name, value);
                                            } else if (group.name === 'awards') {
                                              handleWalkthroughAwardFieldChange(itemIndex, field.name, value);
                                            } else if (group.name === 'social_links') {
                                              handleWalkthroughSocialLinkFieldChange(itemIndex, field.name, value);
                                            }
                                          }}
                                          placeholder={field.placeholder}
                                          disabled={taxonomyLoading || isProcessing}
                                        />
                                      ) : field.type === 'month_year' ? (
                                        <MonthYearPicker
                                          value={item[field.name] || ''}
                                          onChange={(value) => {
                                            if (group.name === 'qualifications') {
                                              handleWalkthroughQualificationFieldChange(itemIndex, field.name, value);
                                            } else if (group.name === 'awards') {
                                              handleWalkthroughAwardFieldChange(itemIndex, field.name, value);
                                            }
                                          }}
                                          placeholder={field.placeholder}
                                          disabled={taxonomyLoading || isProcessing}
                                        />
                                      ) : (
                                        <div className="paragraph_normal_regular flex h-[49px] items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                                          <input
                                            type={field.type}
                                            className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100"
                                            value={item[field.name] || ''}
                                            onChange={(e) => {
                                              if (group.name === 'qualifications') {
                                                handleWalkthroughQualificationFieldChange(itemIndex, field.name, e.target.value);
                                              } else if (group.name === 'awards') {
                                                handleWalkthroughAwardFieldChange(itemIndex, field.name, e.target.value);
                                              } else if (group.name === 'social_links') {
                                                handleWalkthroughSocialLinkFieldChange(itemIndex, field.name, e.target.value);
                                              }
                                            }}
                                            placeholder={field.placeholder}
                                            disabled={taxonomyLoading || isProcessing}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (group.name === 'languages') {
                                        removeWalkthroughLanguage(itemIndex);
                                      } else if (group.name === 'qualifications') {
                                        removeWalkthroughQualification(itemIndex);
                                      } else if (group.name === 'awards') {
                                        removeWalkthroughAward(itemIndex);
                                      } else if (group.name === 'social_links') {
                                        removeWalkthroughSocialLink(itemIndex);
                                      }
                                    }}
                                    className="lg:flex bottom-2.5 right-0 hidden shrink-0 text-brand-orange-600 lg:absolute flex h-8 w-8 items-center justify-center rounded-full border border-solid border-brand-orange-300 transition duration-300 ease-in-out hover:bg-brand-orange-50"
                                    disabled={taxonomyLoading || isProcessing}
                                    title={`Remove ${group.title.toLowerCase().slice(0, -1)}`}
                                  >
                                    <svg width="12" height="4" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path fillRule="evenodd" clipRule="evenodd" d="M0 1.9998C0 1.68154 0.126428 1.37632 0.351472 1.15128C0.576516 0.926233 0.88174 0.799805 1.2 0.799805H10.8C11.1183 0.799805 11.4235 0.926233 11.6485 1.15128C11.8736 1.37632 12 1.68154 12 1.9998C12 2.31806 11.8736 2.62329 11.6485 2.84833C11.4235 3.07338 11.1183 3.1998 10.8 3.1998H1.2C0.88174 3.1998 0.576516 3.07338 0.351472 2.84833C0.126428 2.62329 0 2.31806 0 1.9998Z" fill="currentColor"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                            <div className="flex items-center gap-3 mt-5 self-end">
                              <span className="paragraph_medium_regular text-gray-500">Add {group.title.toLowerCase().slice(0, -1)}</span>
                              <button
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-solid border-gray-300 transition duration-300 ease-in-out hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                type="button"
                                onClick={() => {
                                  if (group.name === 'languages') {
                                    addWalkthroughLanguage();
                                  } else if (group.name === 'qualifications') {
                                    addWalkthroughQualification();
                                  } else if (group.name === 'awards') {
                                    addWalkthroughAward();
                                  } else if (group.name === 'social_links') {
                                    addWalkthroughSocialLink();
                                  }
                                }}
                                disabled={taxonomyLoading || isProcessing}
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M10.5 4.875H7.125V1.5C7.125 1.10156 6.77344 0.75 6.375 0.75H5.625C5.20312 0.75 4.875 1.10156 4.875 1.5V4.875H1.5C1.07812 4.875 0.75 5.22656 0.75 5.625V6.375C0.75 6.79688 1.07812 7.125 1.5 7.125H4.875V10.5C4.875 10.9219 5.20312 11.25 5.625 11.25H6.375C6.77344 11.25 7.125 10.9219 7.125 10.5V7.125H10.5C10.8984 7.125 11.25 6.79688 11.25 6.375V5.625C11.25 5.22656 10.8984 4.875 10.5 4.875Z" fill="#5A5A5A"/>
                                </svg>
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </>
                ) : group.type === 'textarea' ? (
                  <div className="walkthrough-field walkthrough-field-full">
                    <label className="walkthrough-field-label">
                      {group.title}
                      {group.required && isFieldEmpty(anquetData?.[group.name]) && <span className="required-indicator">*</span>}
                    </label>
                    <div className="paragraph_normal_regular flex items-center focus-within:border-gray-500 rounded-md border border-solid border-gray-300 bg-gray-100 px-4 py-3">
                      <textarea
                        value={anquetData?.[group.name] || ''}
                        onChange={(e) => handleWalkthroughFieldChange(group.name, e.target.value)}
                        placeholder={group.placeholder}
                        className="placeholder:text-gray-400 disabled:bg-transparent w-full outline-none bg-gray-100 resize-vertical"
                        onKeyDown={handleKeyDown}
                        rows="6"
                      />
                    </div>
                    {group.example && (
                      <small className="walkthrough-example">Example: {group.example}</small>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GuideContent;
